import {
    Directive,
    effect,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    RaTooltipDirective,
    TooltipConfig,
} from 'src/app/shared/directives/ra-tooltip/ra-tooltip.directive';
import { EditService } from '../../services/edit-service';

@Directive({
    selector: '[raEditTooltip]',
    standalone: true,
})
export class RaEditTooltipDirective extends RaTooltipDirective implements OnInit, OnDestroy {
    private baseTooltip = signal<TooltipConfig | string | null>(null);
    private isBlocked = signal(false);
    private captureHandler?: (event: MouseEvent) => void;

    private _editService = inject(EditService);
    private _translate = inject(TranslateService);

    @Input('raEditTooltip')
    set raEditTooltip(value: TooltipConfig | string | null) {
        this.baseTooltip.set(value);
    }

    constructor(elementRef: ElementRef, renderer: Renderer2) {
        super(elementRef, renderer);

        effect(() => {
            const baseConfig = this.baseTooltip();
            const isLoading = this._editService.loading();
            const loadingKey = this._editService.loadingMessageKey();
            const loadingText = loadingKey ? this._translate.instant(loadingKey) : null;

            this.updateConfig(this.buildTooltipConfig(baseConfig, isLoading, loadingText));
            this.isBlocked.set(isLoading);
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.captureHandler = (event: MouseEvent | TouchEvent | PointerEvent): void => {
            if (!this.isBlocked()) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        };

        this.elementRef.nativeElement.addEventListener('click', this.captureHandler, true);
        this.elementRef.nativeElement.addEventListener('pointerdown', this.captureHandler, true);
        this.elementRef.nativeElement.addEventListener('mousedown', this.captureHandler, true);
        this.elementRef.nativeElement.addEventListener('touchstart', this.captureHandler, true);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this.captureHandler) {
            this.elementRef.nativeElement.removeEventListener('click', this.captureHandler, true);
            this.elementRef.nativeElement.removeEventListener(
                'pointerdown',
                this.captureHandler,
                true
            );
            this.elementRef.nativeElement.removeEventListener(
                'mousedown',
                this.captureHandler,
                true
            );
            this.elementRef.nativeElement.removeEventListener(
                'touchstart',
                this.captureHandler,
                true
            );
        }
    }

    @HostBinding('style.cursor')
    get cursor(): string | null {
        return this.isBlocked() ? 'not-allowed' : null;
    }

    @HostBinding('attr.aria-disabled')
    get ariaDisabled(): 'true' | null {
        return this.isBlocked() ? 'true' : null;
    }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        if (!this.isBlocked()) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
    }

    @HostListener('pointerdown', ['$event'])
    onPointerDown(event: PointerEvent): void {
        super.onMouseDown();

        if (!this.isBlocked()) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
    }

    @HostListener('mousedown', ['$event'])
    override onMouseDown(event?: MouseEvent): void {
        super.onMouseDown();

        if (!this.isBlocked()) {
            return;
        }

        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        }
    }

    @HostListener('touchstart', ['$event'])
    onTouchStart(event: TouchEvent): void {
        super.onMouseDown();

        if (!this.isBlocked()) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
    }

    private buildTooltipConfig(
        baseConfig: TooltipConfig | string | null,
        isLoading: boolean,
        loadingText: string | null
    ): TooltipConfig | string | null {
        if (isLoading) {
            const nextText = loadingText ?? '';

            if (baseConfig && typeof baseConfig !== 'string') {
                return { ...baseConfig, text: nextText };
            }

            return nextText;
        }

        return baseConfig;
    }
}
